import { forwardRef, InputHTMLAttributes } from 'react';
import styled from 'styled-components';
import { Calendar } from 'lucide-react';

interface DateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  hasError?: boolean;
}

const Wrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.base};
  gap: 8px;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.borderFocus};
  }

  > svg {
    color: ${({ theme }) => theme.colors.textMuted};
    flex-shrink: 0;
  }
`;

const Field = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  color-scheme: ${({ theme }) => theme.mode};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ hasError, className, ...rest }, ref) => (
    <Wrapper $hasError={hasError} className={className}>
      <Calendar size={16} aria-hidden="true" />
      <Field
        ref={ref}
        type="date"
        aria-invalid={hasError || undefined}
        {...rest}
      />
    </Wrapper>
  ),
);

DateInput.displayName = 'DateInput';
